import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ScrollText, UserCog } from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return "—"; } };

export default function AuditLog({ lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [entries, setEntries] = useState([]);
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (action) p.set("action", action);
      if (actor) p.set("actor", actor);
      const { data } = await api.get(`/admin/audit-log?${p.toString()}`);
      setEntries(data.entries); setActions(data.actions);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [action, actor]);
  useEffect(() => { load(); }, [load]);

  const summarize = (e) => {
    const m = e.meta || {};
    if (e.action === "property.update" && m.changes) {
      return Object.entries(m.changes).map(([k, v]) => `${k}: ${v.from ?? "—"} → ${v.to ?? "—"}`).join(", ");
    }
    if (e.action === "user.update" && m.changes) return Object.entries(m.changes).map(([k, v]) => `${k}=${v}`).join(", ");
    if (e.action === "data.import") return `${m.filename || ""}${m.rows ? ` · ${m.rows} righe` : ""}`;
    if (e.action === "meal_plan.correct") return `${m.rate_plan_raw} → ${m.standard}`;
    return "";
  };

  return (
    <div data-testid="admin-audit">
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-action-filter"
          className="h-9 rounded border border-border bg-background px-2 text-sm">
          <option value="">{L("Tutte le azioni", "All actions")}</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input value={actor} onChange={(e) => setActor(e.target.value)} placeholder={L("Filtra per email", "Filter by email")}
          data-testid="audit-actor-filter" className="h-9 rounded border border-border bg-background px-2 text-sm w-56" />
      </div>
      {loading ? <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
        <div className="border border-border rounded-lg overflow-x-auto">
          {entries.length === 0 ? <p className="text-sm text-muted-foreground p-4">{L("Nessuna attività registrata.", "No activity recorded.")}</p> : (
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-muted/50"><tr className="text-left">
                {[L("Quando", "When"), L("Chi", "Who"), L("Azione", "Action"), L("Oggetto", "Target"), L("Dettagli", "Details")].map((h) => <th key={h} className="px-3 py-2.5 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} className="border-t border-border align-top" data-testid={`audit-row-${i}`}>
                    <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground text-xs">{fmt(e.at)}</td>
                    <td className="px-3 py-2.5">
                      <span className="break-all">{e.actor_email}</span>
                      {e.impersonator_email && <span className="ml-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-600"><UserCog className="w-3 h-3" />{L("come", "as")} {e.impersonator_email}</span>}
                    </td>
                    <td className="px-3 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full bg-muted font-medium">{(e.action_label || {})[lang === "en" ? "en" : "it"] || e.action}</span></td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{e.target_label || "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground max-w-[280px] break-words">{summarize(e)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
